/**
 * EventDetails Component - Szczegóły wydarzenia
 * wojskaspecjalne.pl
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  getEventById,
  registerForEvent,
  cancelRegistration,
  isUserRegistered,
  type EventWithDetails,
} from '../../lib/eventService';
import { useAuth } from '../../contexts/AuthContext';
import './EventDetails.css';

interface EventDetailsProps {
  eventId: string;
  onEdit?: () => void;
  canEdit?: boolean;
}

export const EventDetails: React.FC<EventDetailsProps> = ({ eventId, onEdit, canEdit = false }) => {
  const { user } = useAuth();
  const [event, setEvent] = useState<EventWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRegistered, setIsRegistered] = useState(false);
  const [registering, setRegistering] = useState(false);

  useEffect(() => {
    loadEvent();
    if (user) {
      checkRegistration();
    }
  }, [eventId, user]);

  const loadEvent = async () => {
    try {
      setLoading(true);
      const result = await getEventById(eventId);

      if (result.success && result.data) {
        setEvent(result.data);
      } else {
        setError(result.error || 'Nie udało się załadować wydarzenia');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const checkRegistration = async () => {
    if (!user) return;

    try {
      const result = await isUserRegistered(eventId, user.id);
      if (result.success) {
        setIsRegistered(result.data || false);
      }
    } catch (err) {
      console.error('Error checking registration:', err);
    }
  };

  const handleRegister = async () => {
    if (!user) {
      alert('Musisz być zalogowany aby zarejestrować się na wydarzenie');
      return;
    }

    try {
      setRegistering(true);
      const result = await registerForEvent(eventId, user.id, {
        payment_status: event?.is_free ? 'free' : 'pending',
      });

      if (result.success) {
        setIsRegistered(true);
        loadEvent(); // Reload to update attendee count
      } else {
        alert(result.error || 'Nie udało się zarejestrować');
      }
    } catch (err: any) {
      alert(err.message);
    } finally {
      setRegistering(false);
    }
  };

  const handleCancelRegistration = async () => {
    if (!user) return;

    if (!confirm('Czy na pewno chcesz anulować rejestrację?')) return;

    try {
      setRegistering(true);
      const result = await cancelRegistration(eventId, user.id);

      if (result.success) {
        setIsRegistered(false);
        loadEvent(); // Reload to update attendee count
      } else {
        alert(result.error || 'Nie udało się anulować rejestracji');
      }
    } catch (err: any) {
      alert(err.message);
    } finally {
      setRegistering(false);
    }
  };

  if (loading) {
    return (
      <div className="event-details">
        <div className="event-details__loading">Ładowanie wydarzenia...</div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="event-details">
        <div className="event-details__error">{error || 'Nie znaleziono wydarzenia'}</div>
      </div>
    );
  }

  const startDate = event.start_date ? new Date(event.start_date) : null;
  const endDate = event.end_date ? new Date(event.end_date) : null;
  const now = new Date();
  const isUpcoming = startDate ? startDate > now : false;
  const isPast = endDate ? endDate < now : false;
  const isFull = event.max_attendees ? event.current_attendees >= event.max_attendees : false;

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('pl-PL', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  };

  const formatTime = (date: Date) => {
    return new Intl.DateTimeFormat('pl-PL', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const isSameDay = startDate && endDate ? startDate.toDateString() === endDate.toDateString() : false;

  return (
    <div className="event-details">
      {/* Cover Image */}
      {event.cover_image_url && (
        <div className="event-details__cover">
          <img src={event.cover_image_url} alt={event.title} />
        </div>
      )}

      {/* Header */}
      <div className="event-details__header">
        {/* Category & Status */}
        <div className="event-details__meta">
          {event.category && (
            <span className="event-details__category">{event.category}</span>
          )}
          {event.status !== 'published' && (
            <span className={`event-details__status event-details__status--${event.status}`}>
              {event.status === 'draft' && 'Szkic'}
              {event.status === 'cancelled' && 'Anulowane'}
              {event.status === 'completed' && 'Zakończone'}
            </span>
          )}
        </div>

        {/* Title */}
        <h1 className="event-details__title">{event.title}</h1>

        {/* Badges */}
        <div className="event-details__badges">
          {event.meeting_link && event.location_name ? (
            <span className="event-details__badge event-details__badge--hybrid">
              Hybrydowe
            </span>
          ) : event.meeting_link ? (
            <span className="event-details__badge event-details__badge--virtual">
              Wirtualne
            </span>
          ) : event.location_name ? (
            <span className="event-details__badge event-details__badge--physical">
              Stacjonarne
            </span>
          ) : null}
          {event.is_free ? (
            <span className="event-details__badge event-details__badge--free">
              Bezpłatne
            </span>
          ) : (
            <span className="event-details__badge event-details__badge--paid">
              {event.price_amount} {event.price_currency}
            </span>
          )}
          {isFull && (
            <span className="event-details__badge event-details__badge--full">
              Brak miejsc
            </span>
          )}
          {isUpcoming && (
            <span className="event-details__badge event-details__badge--upcoming">
              Nadchodzące
            </span>
          )}
          {isPast && (
            <span className="event-details__badge event-details__badge--past">
              Zakończone
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="event-details__actions">
          {canEdit && onEdit && (
            <button onClick={onEdit} className="event-details__btn event-details__btn--secondary">
              Edytuj
            </button>
          )}
          {user && !isPast && !isFull && event.status === 'published' && (
            <>
              {isRegistered ? (
                <button
                  onClick={handleCancelRegistration}
                  disabled={registering}
                  className="event-details__btn event-details__btn--cancel"
                >
                  {registering ? 'Anulowanie...' : 'Anuluj rejestrację'}
                </button>
              ) : (
                <button
                  onClick={handleRegister}
                  disabled={registering}
                  className="event-details__btn event-details__btn--secondary"
                >
                  {registering ? 'Rejestrowanie...' : 'Zarejestruj się'}
                </button>
              )}
            </>
          )}
          {!user && !isPast && event.status === 'published' && (
            <Link to="/login" className="event-details__btn event-details__btn--secondary">
              Zaloguj się aby się zarejestrować
            </Link>
          )}
        </div>
      </div>

      {/* Content Grid */}
      <div className="event-details__grid">
        {/* Main Content */}
        <div className="event-details__main">
          {/* Description */}
          {event.description && (
            <section className="event-details__section">
              <h2 className="event-details__section-title">O wydarzeniu</h2>
              <div className="event-details__description">{event.description}</div>
            </section>
          )}

          {/* Organizers */}
          {event.organizers && event.organizers.length > 0 && (
            <section className="event-details__section">
              <h2 className="event-details__section-title">Organizatorzy</h2>
              <div className="event-details__organizers">
                {event.organizers.map((organizer) => (
                  <div key={organizer.id} className="event-details__organizer">
                    <div className="event-details__organizer-header">
                      <h3 className="event-details__organizer-name">{organizer.organizer_name}</h3>
                      {organizer.organizer_role && (
                        <span className="event-details__organizer-role">
                          {organizer.organizer_role}
                        </span>
                      )}
                    </div>
                    {organizer.organizer_bio && (
                      <p className="event-details__organizer-bio">{organizer.organizer_bio}</p>
                    )}
                    <div className="event-details__organizer-contact">
                      {organizer.organizer_email && (
                        <a href={`mailto:${organizer.organizer_email}`}>
                          📧 {organizer.organizer_email}
                        </a>
                      )}
                      {organizer.organizer_phone && (
                        <a href={`tel:${organizer.organizer_phone}`}>
                          📞 {organizer.organizer_phone}
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Sidebar */}
        <div className="event-details__sidebar">
          {/* Date & Time */}
          {(startDate || endDate) && (
            <div className="event-details__info-card">
              <h3 className="event-details__info-title">Data i Godzina</h3>
              <div className="event-details__info-content">
                {startDate && (
                  <p className="event-details__info-row">
                    <strong>Rozpoczęcie:</strong>
                    <br />
                    {formatDate(startDate)}
                    <br />
                    {formatTime(startDate)}
                  </p>
                )}
                {endDate && (
                  <p className="event-details__info-row">
                    <strong>Zakończenie:</strong>
                    <br />
                    {isSameDay ? formatTime(endDate) : `${formatDate(endDate)}, ${formatTime(endDate)}`}
                  </p>
                )}
                {!startDate && !endDate && (
                  <p className="event-details__info-row">Data do ustalenia</p>
                )}
              </div>
            </div>
          )}

          {/* Online Meeting Link */}
          {event.meeting_link && (
            <a
              href={event.meeting_link}
              target="_blank"
              rel="noopener noreferrer"
              className="event-details__meeting-cta"
            >
              <span className="event-details__meeting-cta-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="7" width="20" height="15" rx="2" ry="2"></rect>
                  <polyline points="17 2 12 7 7 2"></polyline>
                </svg>
              </span>
              <span className="event-details__meeting-cta-content">
                <span className="event-details__meeting-cta-title">
                  Link do spotkania online
                </span>
              </span>
              <span className="event-details__meeting-cta-arrow">→</span>
            </a>
          )}

          {/* Meeting Password */}
          {event.meeting_password && isRegistered && event.meeting_link && (
            <div className="event-details__info-card event-details__meeting-password">
              <h3 className="event-details__info-title">Hasło do spotkania</h3>
              <div className="event-details__password-box">
                <code>{event.meeting_password}</code>
              </div>
              <p className="event-details__hint">
                Zapisz to hasło - będzie potrzebne do dołączenia do spotkania
              </p>
            </div>
          )}

          {/* Location */}
          {(event.location_name || event.organizer_website_link) && (
            <div className="event-details__info-card">
              <h3 className="event-details__info-title">
                {event.meeting_link && event.location_name 
                  ? 'Lokalizacja stacjonarna'
                  : 'Lokalizacja'}
              </h3>
              <div className="event-details__info-content">
                {event.location_name && (
                  <>
                    <p><strong>{event.location_name}</strong></p>
                    {event.location_address && <p>{event.location_address}</p>}
                    {event.location_city && <p>{event.location_city}</p>}
                    {event.location_country && <p>{event.location_country}</p>}
                  </>
                )}
                {event.organizer_website_link && (
                  <p className="event-details__info-row">
                    <a
                      href={event.organizer_website_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="event-details__link"
                    >
                      Strona organizatora
                    </a>
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Price & Attendees */}
          <div className="event-details__info-card">
            <h3 className="event-details__info-title">Szczegóły</h3>
            <div className="event-details__info-content">
              <p className="event-details__info-row">
                <strong>Cena:</strong>
                <br />
                {event.is_free
                  ? 'Bezpłatne'
                  : `${event.price_amount} ${event.price_currency}`}
                {event.price_description && (
                  <>
                    <br />
                    <small>{event.price_description}</small>
                  </>
                )}
              </p>
              {event.max_attendees && (
                <p className="event-details__info-row">
                  <strong>Liczba miejsc:</strong>
                  <br />
                  {event.current_attendees} / {event.max_attendees}
                </p>
              )}
            </div>
          </div>

          {/* Speakers/Prelegenci */}
          {event.speakers && event.speakers.length > 0 && (
            <div className="event-details__info-card">
              <h3 className="event-details__info-title">Prelegenci</h3>
              <div className="event-details__speakers">
                {event.speakers.map((speaker) => (
                  <div key={speaker.id} className="event-details__speaker">
                    <div className="event-details__speaker-info">
                      <strong className="event-details__speaker-name">
                        {speaker.author?.name || 'Nieznany autor'}
                      </strong>
                      {speaker.role && speaker.role !== 'prelegent' && (
                        <span className="event-details__speaker-role">
                          {speaker.role}
                        </span>
                      )}
                      {speaker.author?.bio && (
                        <p className="event-details__speaker-bio">
                          {speaker.author.bio}
                        </p>
                      )}
                      {speaker.author?.website_url && (
                        <a
                          href={speaker.author.website_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="event-details__speaker-link"
                        >
                          Strona autora
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {event.tags && event.tags.length > 0 && (
            <div className="event-details__info-card">
              <h3 className="event-details__info-title">Tagi</h3>
              <div className="event-details__tags">
                {event.tags.map((tag, index) => (
                  <span key={index} className="event-details__tag">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

